import {makeObservable, action, observable, runInAction} from 'mobx';
import {Api} from '@wellstone-solutions/common';
import {defaultPageSize} from '../utils/Paginator';
import {getBoolVariation, LD_FLAGS} from 'modules/feature-flags';

class ChannelStore {
  channels = [];
  unreadMessagesTotal = 0;
  unreadChannels = [];
  isLoading = false;
  hasMore = true;

  constructor(rootStore) {
    makeObservable(this, {
      channels: observable,
      isLoading: observable,
      hasMore: observable,
      loadChannels: action,
      unreadMessagesTotal: observable,
      unreadChannels: observable,
      newChannelActivity: action,
      promoteChannel: action,
      setTotalMessages: action,
    });
    this.rootStore = rootStore;
  }

  // API CALL for page of channels
  loadChannels = async (isRefresh, isUnread, orgId = '') => {
    this.isLoading = true;

    if (isRefresh) {
      this.channels = [];
      this.hasMore = true;
    }

    const params = {
      limit: defaultPageSize,
      offset: this.channels.length,
    };
    if (isUnread) {
      params.unread = 1;
    }
    if (orgId) {
      params.org_id = orgId;
    }

    try {
      const response = await Api.Instance.current().get('/messaging/channels', {
        params,
      });

      runInAction(() => {
        // a short page means we've reached the end of the list
        this.hasMore = response.data.channels.length >= defaultPageSize;
        this.channels = this.channels.concat(
          response.data.channels.filter(
            (value) => !this.channels.find((item) => item.id === value.id),
          ),
        );
      });
    } catch (e) {
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  // API for single channel
  fetchChannel = async (channelId) => {
    const response = await Api.Instance.current().get(
      '/messaging/channels/' + channelId,
    );
    return response.data;
  };

  // scheduling queue task
  scheduleTask = async (channelId, chatMember) => {
    const response = await Api.Instance.current().post(
      '/messaging/channels/' + channelId + '/notification',
      {
        user_id: chatMember,
      },
    );
    return response.data;
  };

  // delete scheduled queue task
  deleteScheduledTask = async (channelId) => {
    const response = await Api.Instance.current().delete(
      '/messaging/channels/' + channelId + '/notification',
    );
    return response.data;
  };

  newChannelActivity = async (channelName) => {
    const channelID = channelName.split('.')[1];
    await this.bumpOrAddChannelToArray(channelID, 'channels');
    await this.bumpOrAddChannelToArray(channelID, 'unreadChannels');
  };

  bumpOrAddChannelToArray = async (channelID, arrName) => {
    let channel = this[arrName].find((c) => c.id === channelID);
    if (!channel) {
      channel = await this.fetchChannel(channelID);
      if (arrName === 'unreadChannels') {
        channel.messageCount = 0;
      }
    }

    this[arrName] = [
      channel,
      ...this[arrName].filter((c) => c.id !== channelID),
    ];
  };

  // Move a channel we already hold to the top of the list, adding it if new
  promoteChannel = (channel) => {
    this.channels = [
      channel,
      ...this.channels.filter((c) => c.id !== channel.id),
    ];
  };

  getChannelByUser = async (user, forceFetch) => {
    let channel = null;
    if (user) {
      this.channels.forEach((c) => {
        if (c.members) {
          c.members.forEach((member) => {
            if (member.id === user) {
              channel = c;
            }
          });
        }
      });

      // Hit the API to get or create the channel for the member
      if (!channel && forceFetch) {
        const response = await Api.Instance.current().post(
          '/messaging/channels',
          {
            members: [{id: user}],
          },
        );

        if (response.isSuccess) {
          channel = response.data;

          // Refresh Pubnub token to include the new channel
          const {pubnubStore} = this.rootStore.stores;
          if (!pubnubStore.hasChannelAuth(channel.name)) {
            await pubnubStore.setTokenForChannel(channel.name);
          }
        }
      }
    }

    return channel;
  };

  getChannelById = async (channelId) => {
    let channel = null;
    if (channelId) {
      this.channels.forEach((c) => {
        if (c.id === channelId) {
          channel = c;
        }
      });
    }

    if (!channel) {
      channel = await this.fetchChannel(channelId);
    }

    return channel;
  };

  updateLocalChannelData = (channelId, channelData) => {
    let channel = this.channels.find((c) => c.id === channelId);
    if (channel) {
      channel.data = {...channel.data, ...channelData};
    }
  };

  updateChannelData = async (channel, channelData) => {
    if (this.rootStore.stores.tutorialStore.isActive) {
      return;
    }

    const params = {
      data: channelData,
    };
    try {
      const response = await Api.Instance.current().post(
        '/messaging/channels/' + channel.id,
        params,
      );
      this.updateLocalChannelData(channel.id, channelData);
      return response;
    } catch (e) {
      console.error('Error updating channel data:', e);
    }
  };

  getUnreadChannels = async () => {
    const params = {unread: 1};
    try {
      // Get unread channels from API
      const response = await Api.Instance.current().get('/messaging/channels', {
        params,
      });

      // Get unread message counts from backend endpoint
      const unreadCountsResponse = await Api.Instance.current().get(
        '/messaging/unread-counts',
      );

      runInAction(() => {
        // Set the unread channels (this list correctly excludes channels
        // where the user sent the latest message)
        this.unreadChannels = response.data.channels;

        // Apply message counts from the backend response
        // Only apply counts to channels that are actually unread
        const counts = unreadCountsResponse.data.channels || {};
        this.unreadChannels.forEach((channel) => {
          // Match channel by name (e.g., "ch-chat.uuid")
          const count = counts[channel.name] || 0;
          channel.messageCount = count;
        });

        // Calculate total from ONLY the unread channels, not the backend total
        // (backend total includes counts for channels where user sent latest message)
        this.setTotalMessages();
      });
    } catch (e) {
      console.log('Error fetching unread channels:', e);
    }
  };

  findUnreadChannel = (name) => {
    let index;
    this.unreadChannels.forEach((c, i) => {
      index = c.name === name ? i : index;
    });
    return index;
  };

  clearUnreadChannel = async (channel) => {
    const index = this.findUnreadChannel(channel.name);
    if (index > -1) {
      this.unreadChannels.splice(index, 1);
      this.setTotalMessages();
    }

    if (channel && getBoolVariation(LD_FLAGS.STAFF_NOTIFICATIONS)) {
      await this.deleteScheduledTask(channel.id);
    }
  };

  addCountToUnreadChannel = (channelName) => {
    const index = this.findUnreadChannel(channelName);
    const unreadChannel = this.unreadChannels[index];
    if (unreadChannel) {
      unreadChannel.messageCount = (unreadChannel.messageCount ?? 0) + 1;
    }
    this.setTotalMessages();
  };

  setTotalMessages = () => {
    const sum = (arr) => {
      return arr.reduce((a, b) => a + (b.messageCount || 0), 0);
    };
    this.unreadMessagesTotal = sum(this.unreadChannels);
  };
}

export default ChannelStore;
